/**
 * usePushSubscription — Iter267
 *
 * React hook that manages the Web Push subscription lifecycle:
 *   • Checks Notification permission
 *   • Fetches the VAPID public key from `/api/smith/push/config`
 *   • Subscribes via ServiceWorkerRegistration.pushManager
 *   • Posts the subscription to `/api/smith/push/subscribe`
 *   • Exposes `subscribe`, `unsubscribe`, `testPush` and a `status`
 *     field: 'idle' | 'checking' | 'granted' | 'denied' | 'unsupported' |
 *              'subscribing' | 'subscribed' | 'error'
 *
 * Returns null-safe values when the browser doesn't support Push (iOS
 * Safari <16.4, older Firefox, private mode, etc.) — components can
 * hide their UI accordingly.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Str = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Str);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

function supported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export default function usePushSubscription() {
  const [status, setStatus] = useState("idle");
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);

  const check = useCallback(async () => {
    if (!supported()) { setStatus("unsupported"); return; }
    setStatus("checking");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        setSubscription(sub);
        setStatus(Notification.permission === "granted" ? "subscribed" : "idle");
      } else {
        setStatus(Notification.permission === "denied" ? "denied" : "idle");
      }
    } catch (e) {
      setError(e); setStatus("error");
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const subscribe = useCallback(async () => {
    if (!supported()) { setStatus("unsupported"); return null; }
    setStatus("subscribing");
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setStatus(perm === "denied" ? "denied" : "idle");
        return null;
      }
      // Prefer server-provided key (single source of truth); fallback env.
      let publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";
      try {
        const { data } = await axios.get(`${API}/smith/push/config`, { timeout: 8000 });
        if (data?.public_key) publicKey = data.public_key;
        if (data && data.enabled === false) {
          setStatus("unsupported"); return null;
        }
      } catch (_e) { /* env fallback is fine */ }
      if (!publicKey) { setStatus("unsupported"); return null; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await axios.post(`${API}/smith/push/subscribe`, {
        subscription: sub.toJSON(),
        user_agent: navigator.userAgent || "",
      });
      setSubscription(sub);
      setStatus("subscribed");
      return sub;
    } catch (e) {
      setError(e); setStatus("error");
      return null;
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    if (!subscription) return;
    try {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await axios.post(`${API}/smith/push/unsubscribe`, { endpoint });
      setSubscription(null);
      setStatus("idle");
    } catch (e) { setError(e); setStatus("error"); }
  }, [subscription]);

  const testPush = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/smith/push/test`);
      return data;
    } catch (e) { setError(e); return null; }
  }, []);

  return {
    status,
    subscription,
    error,
    supported: supported(),
    permission: supported() ? Notification.permission : "denied",
    subscribe,
    unsubscribe,
    testPush,
    refresh: check,
  };
}
